import { useCapture } from "@suse-o11y/analytics/use-capture";
import { DialogBoundary, DialogTrigger } from "@suse-o11y/dialog";
import { IconAssistant } from "@suse-o11y/icons";
import React, { type FC } from "react";
import { useLocation } from "react-router-dom";

import { Truncated } from "~/components/truncated";

import { SystemNotificationCenter } from "../../components/_system-notifications";
import { DocLink } from "../../components/react/docLink/DocLink";
import { UpgradeButton } from "../../components/react/upgrade-button/UpgradeButton";
import { matchPath } from "../../generic/routing";
import { queryLicenseConfig } from "../../queries";
import { useQueryTopNavCtaIntroText } from "../../queries/_contentful";
import { useQuery } from "../../queries/react-query";
import { COMPONENT_PATH } from "../../routes/Routes";
import { NBSP } from "../../routes/welcome-page/WelcomeLayout";
import { AiAssistantDrawer } from "../../sessions/ai-assistant-drawer/ai-assistant-drawer";
import { useAiAssistantAvailability } from "../../sessions/use-ai-assistant-availability";
import { AccountInfo } from "../AccountInfo/AccountInfo";

import { OnlineTrialMessage } from "./OnlineTrialMessage";
import { TopNav } from "./TopNav";

export interface TopNavigationProps {
	isFormAuth: boolean;
	isPlayground: boolean;
}

export const TopNavigation: FC<TopNavigationProps> = ({ isFormAuth, isPlayground }) => {
	const location = useLocation();
	const componentIdentifier = matchPath(location.pathname, COMPONENT_PATH)?.params.identifier;

	const {
		data: { isTrial, isCommunity, daysLeftToExpire } = {
			isTrial: false,
			isCommunity: false,
			daysLeftToExpire: undefined,
		},
	} = useQuery(queryLicenseConfig);

	const enableAi = useAiAssistantAvailability();

	return (
		<div className="absolute inset-x-0 top-0 z-100 h-10 bg-core-layer-brand-secondary" data-testid="top-navigation">
			<nav className="flex flex-row items-center text-13 text-white leading-none">
				<TopNav />
				<div className="ml-auto flex shrink-0 flex-row items-center justify-end gap-4 pr-1">
					{isCommunity && <UpgradeToRancherPrimeAction className="mr-2" />}
					{isPlayground && <PlaygroundCallToAction />}
					{isTrial && daysLeftToExpire != null && <OnlineTrialMessage daysLeftToExpire={daysLeftToExpire} />}
					{enableAi && (
						<DialogBoundary>
							<DialogTrigger aria-label="Open AI Assistant" className="button button-square">
								<IconAssistant className="size-5" />
							</DialogTrigger>

							<AiAssistantDrawer _kind="chat" componentIdentifier={componentIdentifier} />
						</DialogBoundary>
					)}
					<SystemNotificationCenter />
					<div data-testid="user-account">
						<AccountInfo guiAuth={isFormAuth} />
					</div>
				</div>
			</nav>
		</div>
	);
};

const UpgradeToRancherPrimeAction: FC<{ className?: string }> = ({ className }) => {
	const capture = useCapture();

	return (
		<Truncated className={className}>
			<DocLink
				capture={[capture.clickOnUpgradeToPrime, { location: "navbar" }]}
				slug="rancher-prime-upgrade"
				theme="light"
			>
				Upgrade to Rancher Prime
			</DocLink>{" "}
			<span className="text-grey-500">and enjoy faster troubleshooting</span>
		</Truncated>
	);
};

const PlaygroundCallToAction: FC = () => {
	const { data: ctaText } = useQueryTopNavCtaIntroText({ select: ({ fields }) => fields });

	return (
		<div className="flex h-10 flex-row items-center overflow-hidden">
			<Truncated data-testid="upgrade-button-intro-text">{ctaText?.value ?? NBSP}</Truncated>
			<div className="px-4" data-testid="upgrade-button">
				<UpgradeButton />
			</div>
		</div>
	);
};
